import { useFormContext } from 'react-hook-form';
import { categoryColor } from '@/shared/lib/category-color';
import { ROW_ICON_BUTTON } from '@/components/blocks/row-actions';
import { useEffect, useRef, useState } from 'react';
import {
  Clock,
  X,
  ChevronDown,
  Plus,
  Pencil,
  Building2,
  Home,
  Video,
  MapPin,
  Search,
  Check,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAppointmentProfiles } from '@/services/scheduling/use-appointment-profiles';
import { useServiceFees, type ServiceFee } from '@/services/billing/use-service-fees';
// Cross-domain import (scheduling → billing) — an established, shipped
// pattern in this module (see edit-insurance-view.tsx + create-patient-view.tsx).
// The org billing settings carry `followUpValidity`, needed for the
// free-of-charge follow-up banner (F-SCH-137).
import { useBillingSettings } from '@/billing/services/use-billing-settings';
import { isFreeFollowUpEligible } from '@/scheduling/lib/free-followup';
import {
  usePreBookAppointmentProfile,
  pickDefaultReminder,
  pickAttachedBillingProfile,
  reminderRules,
  formatBillingProfile,
  billingProfileList,
  type PreBookAppointmentProfileResponse,
} from '@/services/scheduling/use-prebook-appointment-profile';
import { usePreBookSelectedSlot } from '@/services/scheduling/use-prebook-selected-slot';
import { useProviderReasonForVisits } from '@/shared/hooks/use-provider-reason-for-visits';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';

interface AppointmentProfileSectionProps {
  organizationId: string;
  practiceId: string;
  /**
   * EDIT mode (not reschedule): keep the appointment's loaded reminder when
   * the profile hydrates, instead of replacing it with the practice default.
   * Create / quick-create / reschedule leave this off so a profile's own
   * default reminder still wins, as before.
   */
  keepLoadedReminder?: boolean;
  /**
   * When set (RESCHEDULE mode), the picker stays enabled but only lists
   * profiles whose appointment-type matches this code — the user may swap to a
   * different same-type profile/slot but cannot change the appointment's type.
   * Mirrors Flutter `appointment_profile_widget.dart` reschedule constraint.
   * Null/undefined (create/edit) leaves the list unconstrained by type.
   *
   * NOTE: intentionally a UI-only constraint (same posture as Flutter and the
   * create/edit picker) — it narrows what the dropdown surfaces, not a backend
   * guard. Reschedule save validation is unchanged from create/edit.
   */
  constrainToAppointmentTypeCode?: string | null;
  // EDIT-mode lock matrix. Flutter does NOT lock this section as one unit —
  // `AppointmentProfileWidget` takes a single `showEdit` flag but applies it
  // ONLY to the profile card's pencil/delete row, while the Reason-for-visit
  // field and the billing row each carry their OWN independent gate. Porting
  // the section as one `SectionLock` collapsed three gates into the narrowest
  // one and made an appointment with no RFV permanently un-editable (see
  // CONVERSION_NOTES.md §5).
  /** Profile card pencil / clear-× / alternative-profile grid.
   *  Flutter `showEdit` — `create_appointment_widget.dart:1134`:
   *  `id == null || status ∈ {Booked, Proposed, Pending}`. */
  profileEditLocked?: boolean;
  /** Reason-for-visit picker. Flutter `appointment_profile_widget.dart:365-373`
   *  opens the RFV popup whenever `appointmentReason.length != 0 && status !=
   *  Cancelled` — deliberately NOT under `showEdit`, so an Arrived / CheckedIn /
   *  Fulfilled / NoShow appointment can still have its RFV corrected. */
  reasonLocked?: boolean;
  /** Billing-profile dropdown + Add-procedure-codes button. Flutter
   *  `appointment_profile_widget.dart:408-415`:
   *  `(invoiceStatus != Draft && id != null) || status ∈ {Cancelled, NoShow}`. */
  billingLocked?: boolean;
}

// The profile's picked colour is drawn in the muted Okabe-Ito ramp at display (shared/lib/category-color).
// PROFILE_COLOR_FALLBACK_HEX mirrors --color-text-hint for unparseable input.
const PROFILE_COLOR_FALLBACK_HEX = '#AFBAC0';

/**
 * AppointmentProfileSection — picks the profile that drives slot duration,
 * reasonCode, room, billing, and appointmentType. Renders only after a
 * provider is selected (mirrors Flutter `appointment_profile_widget.dart`).
 */
export function AppointmentProfileSection({
  organizationId,
  practiceId,
  constrainToAppointmentTypeCode,
  keepLoadedReminder = false,
  profileEditLocked = false,
  reasonLocked = false,
  billingLocked = false,
}: AppointmentProfileSectionProps) {
  const { watch, setValue, getValues } = useFormContext<AppointmentFormValues>();
  const providerId = watch('providerId');
  const selectedDate = watch('selectedDate');
  const selectedProfileId = watch('appointmentProfileId');
  const selectedProfileName = watch('appointmentProfileName');
  const selectedColor = watch('appointmentProfileColor');
  const selectedDuration = watch('minutesDuration');
  const selectedReason = watch('reasonCode');
  const reasonOptions = watch('reasonCodeOptions');
  const location = watch('location');
  const billingProfileName = watch('billingProfileName');
  const billingProfileId = watch('billingProfileId');
  const billingProfilesAll = watch('billingProfilesList');

  // Local state for the three picker popovers — controlled so the
  // search inputs reset between opens and we can close on selection.
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonSearch, setReasonSearch] = useState('');
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingSearch, setBillingSearch] = useState('');
  const [procedureOpen, setProcedureOpen] = useState(false);
  // Edit mode — expands the alternative-profile grid below the
  // selected card so the user can switch profiles without first
  // clearing (Flutter `appointment_profile_widget.dart` "edit" flow).
  const [profileEditOpen, setProfileEditOpen] = useState(false);

  // RFV options — TWO sources, mirroring Flutter. `reasonCodeOptions` is filled
  // by the PreBook chain (`providerReasonForVisits` on the response, Flutter
  // `create_appointment_widget.dart:2273`); this query is the second source
  // Flutter also carries, `AppointmentReasonValueSetEvent` → `GET
  // /api/ProfessionalDetails/GetProviderReasonForVisits/{providerId}`
  // (`create_appointment_widget.dart:2182` → `:2228`).
  //
  // Why both: PreBook only writes the form field when its response carries a
  // non-empty list, and in EDIT mode the hydration effect below fires at most
  // once per profile id. Any miss (PreBook error, an already-populated billing
  // list short-circuiting the effect, a profile whose reasonCode is null) left
  // `reasonCodeOptions` empty — an empty picker, and with the schema's
  // `reasonCode required` rule that means Save can never be re-enabled.
  // Verified on dev 2026-09-09: both endpoints return the SAME list for a
  // provider, so unioning them cannot introduce an option Flutter wouldn't show.
  const { data: providerRfv } = useProviderReasonForVisits(providerId);

  const { data, isLoading } = useAppointmentProfiles(
    providerId ? { organizationId, practiceId, providerId } : null,
  );
  const allProfiles = Array.isArray(data) ? data : [];
  // Flutter parity: only profiles whose `location.name` matches the
  // currently picked location are shown (Office picks → Office profiles
  // only, Virtual → Virtual, Home → Home). Compare case-insensitively
  // because some endpoints ship the name capitalised, others not.
  const selectedLocationName = (location?.name ?? '').toLowerCase();
  // PROD-920 — when entering this panel from the FU Requests "Create
  // Follow Up" kebab, `referenceAppointmentId` is pre-set on the form
  // (frame-scheduling.tsx#handleFuCreateFollowUp). That's the
  // unambiguous signal that we're in the follow-up flow — restrict the
  // profile picker to FOLLOWUP-type profiles only. Mirrors Flutter
  // `calendar_screen.dart:182` (`selectedAppointmentType:
  // appointmentTypeFollowUp`) → downstream Flutter widget filters
  // profiles by that locked type.
  const referenceAppointmentId = watch('referenceAppointmentId');
  const isFollowUpFlow = !!referenceAppointmentId;
  const profiles = allProfiles.filter((p) => {
    if (
      selectedLocationName &&
      (p.location?.name ?? '').toLowerCase() !== selectedLocationName
    ) {
      return false;
    }
    if (isFollowUpFlow) {
      // PascalCase tolerance — some appointment-profile endpoints ship
      // the appointmentType keys capitalised (same as `selectProfile`
      // below handles).
      const at = p.appointmentType as
        | (typeof p.appointmentType & { Code?: string })
        | undefined;
      const code = at?.code ?? at?.Code;
      if (code !== 'FOLLOWUP') return false;
    }
    if (constrainToAppointmentTypeCode) {
      // RESCHEDULE mode — restrict to the same appointment-type as the
      // appointment being moved (same PascalCase tolerance as above).
      const at = p.appointmentType as
        | (typeof p.appointmentType & { Code?: string })
        | undefined;
      const code = at?.code ?? at?.Code;
      if (code !== constrainToAppointmentTypeCode) return false;
    }
    return true;
  });

  // Mutation that fires the PreBook chain on profile pick — auto-populates
  // reminderProfile (required for save) + billingProfile.
  const preBook = usePreBookAppointmentProfile();
  // Slot-mode variant — Flutter `calendar_screen.dart:347-353` dispatches
  // `PreBookSelectedSlotEvent` instead when a slot is already known.
  // Both endpoints return identical response shape but
  // `PreBookSelectedSlot` populates `providerReasonForVisits` more
  // reliably for the slot case.
  const preBookSlot = usePreBookSelectedSlot();
  const slotStart = watch('slotStart');
  // F-SCH-137 inputs — selected profile type, the previous-appointment date
  // (follow-up flow), and the org's follow-up validity window.
  const appointmentType = watch('appointmentType');
  const previousAppointmentDate = watch('previousAppointmentDate');
  const { data: billingSettings } = useBillingSettings(organizationId);

  /**
   * Quick-create hydration (PROD-829): when the panel mounts with a
   * profile already selected (via `initialValues` from
   * `frame-scheduling.tsx#handleQuickCreate`), the user didn't click
   * `selectProfile()` so the PreBook chain never ran. The reminder /
   * billing / reasonOptions fields stay empty, the reason field
   * shows no options to pick from, and Save validation can't fire.
   *
   * This effect detects "profile is selected but reasonCodeOptions is
   * empty" — a clear hydrated-without-prebook signature — and fires
   * the same PreBook mutation the section's `selectProfile()` runs.
   * `hydratedRef` guards against re-firing if the user navigates away
   * and back.
   */
  // Pre-filled profile carried in by quick-create (PROD-829) — `pickedProfile`
  // holds the FULL `CreateAppointmentProfileRequest` (including the embedded
  // `billingProfile` object). This is the canonical source for billing
  // lookup in the hydration path below — reading from `allProfiles.find()`
  // hits a race condition where the GET `/api/Doctor/AppointmentProfileList`
  // hasn't resolved yet when the PreBook response arrives, so the closure
  // sees an empty list. `form.pickedProfile` is set synchronously by
  // `handleQuickCreate` (frame-scheduling.tsx) so it's available from the
  // first effect run.
  const pickedProfile = watch('pickedProfile') as
    | (CreateAppointmentProfileRequest & {
        billingProfile?: { id?: string; name?: string; totalCost?: number; totalAmount?: number } | null;
      })
    | null
    | undefined;

  const hydratedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedProfileId || !providerId || !selectedDate) return;
    if (hydratedRef.current === selectedProfileId) return;
    // Skip when the section's own `selectProfile()` already populated.
    if (reasonOptions.length > 0 || (billingProfilesAll?.length ?? 0) > 0) return;
    hydratedRef.current = selectedProfileId;

    const handlePreBookResponse = (response: PreBookAppointmentProfileResponse) => {
      const reminder = pickDefaultReminder(response.reminderList);
      // EDIT: keep the reminder the appointment already has. This hydration
      // ran over the loaded `reminderProfileId` with the practice's default
      // template, so every save looked like a reminder change and the backend
      // created a fresh reminder row (prod, 2026-10-01: three saves, three new
      // ReminderProfileIds). Flutter keeps the loaded one.
      if (reminder?.id && !(keepLoadedReminder && getValues('reminderProfileId'))) {
        setValue('reminderProfileId', reminder.id, { shouldValidate: true });
        setValue('reminderProfileName', reminder.profileName ?? null);
        setValue('reminderProfileRules', reminderRules(reminder));
        setValue('reminderProfile', reminder);
      }
      const billingList = billingProfileList(response.billingProfiles);
      setValue('billingProfilesList', billingList);
      // Source priority for the picked profile (Flutter parity):
      //   1. `pickedProfile` from form — set by quick-create's
      //      `buildProfileFormValues(profile)`. Carries the FULL profile
      //      including the embedded `billingProfile` object. Available
      //      from the first render.
      //   2. `response.appointmentProfileResponseList.createAppointmentProfileRequest`
      //      — the FRESH profile list bundled with the PreBook response
      //      (Flutter `create_appointment_widget.dart:2275-2280` reads
      //      from here). Always up-to-date.
      //   3. `allProfiles.find()` — fallback to the GET list. Races the
      //      PreBook response on cold cache so it's last resort.
      // `appointmentProfileResponseList` is typed as `unknown` on the
      // shared response type (different endpoints ship variant shapes).
      // Cast through the known runtime shape — Flutter
      // `create_appointment_widget.dart:2275-2280` reads this same path.
      //
      // Runtime guards (Samer review §2 — defend against malformed
      // responses that would otherwise silently fall through to
      // `allProfiles.find()` and re-surface Bug C): require
      // `appointmentProfileResponseList` to be a non-null object AND
      // `createAppointmentProfileRequest` to be an array before
      // attempting the lookup.
      const aplRaw = response.appointmentProfileResponseList;
      const apl =
        aplRaw && typeof aplRaw === 'object' && !Array.isArray(aplRaw)
          ? (aplRaw as { createAppointmentProfileRequest?: unknown })
          : undefined;
      const aplList = Array.isArray(apl?.createAppointmentProfileRequest)
        ? (apl.createAppointmentProfileRequest as CreateAppointmentProfileRequest[])
        : undefined;
      const responseFreshProfile = aplList?.find(
        (p) => p?.id === selectedProfileId,
      );
      const fullProfile =
        pickedProfile ?? responseFreshProfile ?? allProfiles.find((p) => p.id === selectedProfileId);
      if (fullProfile) {
        const billing = pickAttachedBillingProfile(fullProfile, billingList);
        if (billing?.id) {
          setValue('billingProfileId', billing.id);
          setValue('billingProfileName', formatBillingProfile(billing));
        }
      }
      // Dropdown options come from `providerReasonForVisits`. Merge in
      // the picked profile's own reasonCode so the currently-selected
      // value (which `buildProfileFormValues` seeds from
      // `profile.reasonCode`) always has a matching dropdown entry —
      // otherwise the picker opens with no item highlighted as
      // selected, even though the field DISPLAYS the profile's reason.
      // Dedupe by `code` (preferred) then by `display` so a backend
      // that ships the same RFV in both lists doesn't duplicate.
      const remoteOptions = (response.providerReasonForVisits ?? []).map((r) => ({
        code: r.code,
        display: r.display,
        definition: r.definition,
      }));
      const profileReason = fullProfile?.reasonCode;
      const profileReasonCanonical =
        profileReason?.code && profileReason?.display
          ? {
              code: profileReason.code,
              display: profileReason.display,
              definition: profileReason.definition ?? '',
            }
          : null;
      const merged = profileReasonCanonical
        ? [
            profileReasonCanonical,
            ...remoteOptions.filter(
              (r) =>
                r.code !== profileReasonCanonical.code &&
                (r.display ?? '').toLowerCase() !==
                  (profileReasonCanonical.display ?? '').toLowerCase(),
            ),
          ]
        : remoteOptions;
      if (merged.length > 0) {
        setValue('reasonCodeOptions', merged);
      }
    };

    // Route by slot presence — Flutter parity:
    //   slot present → PreBookSelectedSlot (Case A/B, calendar_screen.dart:347)
    //   slot null    → PreBookSelectedAppointmentProfile (Case C, :386)
    //
    // Use `mutateAsync().then()` instead of `mutate(opts, callbacks)`:
    // RQ v5 per-call callbacks are skipped when the calling component
    // unmounts mid-flight (StrictMode dev double-mount, HMR module
    // replacement, etc.). The promise from mutateAsync resolves
    // regardless, so the hydration always reaches its setValue calls
    // even when the dev tooling cycles the tree.
    if (slotStart) {
      preBookSlot
        .mutateAsync({
          organizationId,
          practiceId,
          providerId,
          appointmentProfileId: selectedProfileId,
        })
        .then(handlePreBookResponse)
        .catch(() => {
          // Global error toast in api-client surfaces the failure; just
          // unblock the hydratedRef so a retry on next mount can refire.
          hydratedRef.current = null;
        });
    } else {
      preBook
        .mutateAsync({
          organizationId,
          practiceId,
          providerId,
          appointmentProfileId: selectedProfileId,
          date: selectedDate,
        })
        .then(handlePreBookResponse)
        .catch(() => {
          hydratedRef.current = null;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfileId, providerId, selectedDate, slotStart]);

  // Union the two option sources, then guarantee the CURRENTLY-SELECTED value
  // is present. Dev shows appointment RFVs that are no longer in the provider's
  // configured list (appointment `surgery` vs provider list `Week 1 Visit`), so
  // without the self-insert the picker opens with nothing highlighted even
  // though the field displays a value. Dedupe by lowercased code.
  const reasonChoices = (() => {
    const out: { code: string; display?: string; definition?: string | null }[] = [];
    const seen = new Set<string>();
    // `code`/`display` are nullable on `ProviderReasonForVisit` (the GET ships
    // nulls) but non-null on the form's `codingSchema`, so accept the loose
    // shape and drop anything without a code.
    const push = (
      r?: {
        code?: string | null;
        display?: string | null;
        definition?: string | null;
      } | null,
    ) => {
      const code = r?.code;
      if (!code) return;
      const k = code.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push({ code, display: r?.display ?? undefined, definition: r?.definition ?? null });
    };
    reasonOptions.forEach(push);
    (providerRfv ?? []).forEach(push);
    push(selectedReason);
    return out;
  })();

  const reasonPickerDisabled = reasonLocked || reasonChoices.length === 0;

  if (!providerId) return null;

  // Show the selected card whenever the form has a selection, even if the
  // GET list is empty/stale (e.g. profile was auto-picked from the inline
  // ProviderSlots response without a separate AppointmentProfileList round-trip).
  const isSelected = !!selectedProfileId;
  // Resolve from the unfiltered list — the selected card needs to render
  // with its real color even if the user changes location to one that
  // excludes the picked profile.
  const fromList = allProfiles.find((p) => p.id === selectedProfileId);
  const dotColor = selectedColor ?? fromList?.color;

  // F-SCH-137: "Eligible for a free of charge follow-up." banner — Flutter
  // `appointment_profile_widget.dart:633-680`. Eligibility (follow-up profile +
  // within the org's followUpValidity window of the previous appointment) is a
  // pure function so the Dart `.inDays` parity is unit-tested. Slot start must
  // exist (Flutter gates on `start != null`), so the banner only appears once a
  // slot is picked.
  const showFreeFollowUpBanner = isFreeFollowUpEligible({
    appointmentTypeCode: appointmentType?.code,
    previousAppointmentDate,
    slotStart,
    followUpValidityDays: billingSettings?.followUpValidity ?? 0,
  });

  const selectProfile = (profile: CreateAppointmentProfileRequest) => {
    setValue('appointmentProfileId', profile.id ?? null, { shouldValidate: true });
    setValue('appointmentProfileName', profile.profileName);
    setValue('appointmentProfileColor', profile.color ?? null);
    // Tolerate PascalCase keys (`Code`/`Display`/`Definition`) — some
    // appointment-profile endpoints ship them that way. Drop the value
    // entirely when neither shape yields a code, otherwise the schema's
    // `code: z.string()` rejects an `undefined` and Save stays disabled
    // with no obvious fix.
    const at = profile.appointmentType as
      | (typeof profile.appointmentType & {
          Code?: string;
          Display?: string;
          Definition?: string;
        })
      | undefined;
    const atCode = at?.code ?? at?.Code;
    const atDisplay = at?.display ?? at?.Display;
    setValue(
      'appointmentType',
      atCode && atDisplay
        ? {
            code: atCode,
            display: atDisplay,
            definition: at?.definition ?? at?.Definition ?? '',
          }
        : null,
      { shouldValidate: true },
    );
    // Same PascalCase tolerance as `appointmentType` above — some
    // profile-list endpoints ship `ReasonCode` capitalised. Coerce to
    // null when neither shape yields a code/display so the schema's
    // `reasonCode.code required` rule (Flutter saveAppointmentButton
    // Status:1336) doesn't fire on a half-shaped value.
    const rc = profile.reasonCode as
      | (typeof profile.reasonCode & {
          Code?: string;
          Display?: string;
          Definition?: string;
        })
      | undefined;
    const rcCode = rc?.code ?? rc?.Code;
    const rcDisplay = rc?.display ?? rc?.Display;
    const profileReasonCode =
      rcCode && rcDisplay
        ? {
            code: rcCode,
            display: rcDisplay,
            definition: rc?.definition ?? rc?.Definition ?? '',
          }
        : null;

    // Reason-code carve-out for the FU Create Follow Up flow — Flutter
    // parity, `create_appointment_widget.dart:1089-1102`:
    //
    //   if (profile.appointmentType.code == "FOLLOWUP"
    //       && appointment.previousAppointmentRFV != null) {
    //     if (appointment.reasonCode != null) {
    //       // PRESERVE existing reasonCode; push it onto the profile.
    //       appointment.appointmentProfile.reasonCode = appointment.reasonCode;
    //     } else {
    //       appointment.reasonCode = profile.reasonCode;
    //     }
    //   } else {
    //     appointment.reasonCode = profile.reasonCode;  // normal flow
    //   }
    //
    // React signals "FU flow" via referenceAppointmentId non-null
    // (same convention the profile-filter above uses). When in FU flow
    // AND form already has a reasonCode (seeded by
    // `handleFuCreateFollowUp` from `row.reasonCode`), preserve it
    // and patch the picked profile's reasonCode so the wire
    // appointmentProfile.reasonCode also carries the FU request's
    // RFV (consistent top-level + nested values).
    const existingReasonCode = watch('reasonCode');
    const inFollowUpFlow = !!referenceAppointmentId;
    const preserveFuRfv =
      inFollowUpFlow && atCode === 'FOLLOWUP' && existingReasonCode != null;

    if (preserveFuRfv) {
      // Don't touch form.reasonCode — keeps the FU request's RFV.
      // Patch the picked profile so the wire's
      // appointmentProfile.reasonCode also carries it.
      setValue('pickedProfile', {
        ...profile,
        reasonCode: existingReasonCode,
      });
    } else {
      setValue('pickedProfile', profile);
      setValue(
        'reasonCode',
        profileReasonCode,
        // shouldValidate: true so the schema re-runs against the new
        // reasonCode. Earlier setValue calls in this function pass
        // shouldValidate, which fired validation while reasonCode was
        // still empty — leaving a stale "required" error.
        { shouldValidate: true },
      );
    }
    setValue('minutesDuration', profile.minutesDuration ?? null);
    // shouldValidate on the slot clears: without it the form's cached validity
    // keeps Book ENABLED after a profile change wipes the slot — the submit then
    // ships an empty end (500'd on dev 2026-08-11 before the backend hardening;
    // now a SlotRequired 400, but the button must not offer a doomed submit).
    setValue('slotId', null, { shouldValidate: true });
    setValue('slotStart', null, { shouldValidate: true });
    setValue('slotEnd', null, { shouldValidate: true });

    // Clear billing context before the new PreBook fires — switching to
    // a profile WITHOUT an attached billing profile must drop the
    // previous profile's billing, not silently inherit it. The PreBook
    // `.then()` below restores billing for the new profile if attached.
    setValue('billingProfileId', null);
    setValue('billingProfileName', null);
    setValue('billingProfilesList', []);

    if (profile.id && providerId && selectedDate) {
      // Use mutateAsync().then() — RQ v5 per-call onSuccess can skip
      // when StrictMode/HMR cycles the tree mid-flight. mutateAsync's
      // promise resolves regardless.
      preBook
        .mutateAsync({
          organizationId,
          practiceId,
          providerId,
          appointmentProfileId: profile.id,
          date: selectedDate,
        })
        .then((response) => {
          const reminder = pickDefaultReminder(response.reminderList);
          if (reminder?.id) {
            setValue('reminderProfileId', reminder.id, { shouldValidate: true });
            setValue('reminderProfileName', reminder.profileName ?? null);
            setValue('reminderProfileRules', reminderRules(reminder));
            setValue('reminderProfile', reminder);
          }
          const billingList = billingProfileList(response.billingProfiles);
          setValue('billingProfilesList', billingList);
          // Flutter sources the billing profile from the embedded
          // `appointmentProfile.billingProfile` (create_appointment_widget
          // .dart:2258). `pickAttachedBillingProfile` reads the embedded
          // object first, then falls back to a list lookup by
          // `billingProfileId` for endpoints that only ship the id.
          const billing = pickAttachedBillingProfile(profile, billingList);
          if (billing?.id) {
            setValue('billingProfileId', billing.id);
            setValue('billingProfileName', formatBillingProfile(billing));
          }
          // Merge the picked profile's reasonCode into the dropdown
          // options so the currently-selected RFV ("surgery" etc.)
          // shows as highlighted when the picker opens. Same dedupe
          // strategy as the hydration effect above.
          const remoteOptions = (response.providerReasonForVisits ?? []).map((r) => ({
            code: r.code,
            display: r.display,
            definition: r.definition,
          }));
          const profileReasonCanonical =
            profileReasonCode && profileReasonCode.code && profileReasonCode.display
              ? {
                  code: profileReasonCode.code,
                  display: profileReasonCode.display,
                  definition: profileReasonCode.definition ?? '',
                }
              : null;
          const merged = profileReasonCanonical
            ? [
                profileReasonCanonical,
                ...remoteOptions.filter(
                  (r) =>
                    r.code !== profileReasonCanonical.code &&
                    (r.display ?? '').toLowerCase() !==
                      (profileReasonCanonical.display ?? '').toLowerCase(),
                ),
              ]
            : remoteOptions;
          if (merged.length > 0) {
            setValue('reasonCodeOptions', merged);
          }
          // Auto-open the reason picker so the user's next step is clear.
          if (!reasonLocked) setReasonOpen(true);
        })
        .catch(() => {
          // Global error toast in api-client surfaces failure.
        });
    } else {
      // No PreBook — open reason picker immediately if there are options.
      if (!reasonLocked) setReasonOpen(true);
    }
  };

  const clearProfile = () => {
    setValue('appointmentProfileId', null, { shouldValidate: true });
    setValue('appointmentProfileName', null);
    setValue('appointmentProfileColor', null);
    setValue('appointmentType', null, { shouldValidate: true });
    // `shouldValidate` is load-bearing, not decoration. The Save gate is
    // `Object.keys(formState.errors).length === 0`, and with a resolver RHF's
    // field-level validation only writes the error for the field it was given.
    // Nulling `reasonCode` WITHOUT validating therefore leaves the form holding
    // a null reason and NO `reasonCode` error — the gate reads open, and
    // `transform-form-to-request` then ships `{ code: '', … }`, which the API
    // accepts (200) and stores as an empty reason that no client can later
    // repair. Verified in `appointment-profile-clear-validates.test.tsx`.
    setValue('reasonCode', null, { shouldValidate: true });
    setValue('reasonCodeOptions', []);
    setValue('minutesDuration', null);
    setValue('pickedProfile', null);
    setValue('reminderProfileId', null, { shouldValidate: true });
    setValue('reminderProfileName', null);
    setValue('reminderProfileRules', []);
    setValue('billingProfileId', null);
    setValue('billingProfileName', null);
    // Same shouldValidate reasoning as the profile-select path above.
    setValue('slotId', null, { shouldValidate: true });
    setValue('slotStart', null, { shouldValidate: true });
    setValue('slotEnd', null, { shouldValidate: true });
  };

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <p className="text-xs font-medium text-text-muted mb-2">
        Appointment profile
      </p>

      {isSelected ? (
        <>
        <div className="rounded-lg border border-border-default bg-surface-card overflow-hidden">
          {/* Header row: color dot + name + duration · location + edit + delete */}
          <div className="flex items-start gap-3 p-3">
            <span
              aria-hidden
              className="w-2 h-2 rounded-full flex-shrink-0 mt-2"
              style={{ backgroundColor: categoryColor(parseColor(dotColor), 500, 'var(--cat-blue-500)') }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary leading-snug">
                {selectedProfileName}
              </p>
              <p className="text-xs text-text-muted flex items-center gap-1 mt-1">
                <Clock className="h-3 w-3" />
                <span>{selectedDuration ?? '–'} minutes</span>
                {location?.name ? (
                  <>
                    <span className="text-text-hint">·</span>
                    <span>{location.name}</span>
                  </>
                ) : null}
              </p>
            </div>
            <button
              type="button"
              disabled={profileEditLocked}
              onClick={() => setProfileEditOpen((v) => !v)}
              className={cn(ROW_ICON_BUTTON, profileEditOpen ? 'border-brand-primary text-brand-primary' : 'text-text-secondary')}
              aria-label="Edit profile"
              aria-pressed={profileEditOpen}
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={profileEditLocked}
              onClick={clearProfile}
              className={cn(ROW_ICON_BUTTON, 'text-status-error')}
              aria-label="Clear profile"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Reason for visit — Flutter parity opens a popup with a
              search and clickable rows (appointment_profile_widget.dart
              showRFVPopup). The trigger is a styled "text field" that
              shows the resolved display label or a placeholder.
              Always rendered once a profile is selected so the user
              sees the field even before pre-book lands (Flutter
              "Enter reason for visit" with validation message). */}
          {true ? (
            <div className="px-3 pb-2" data-completion-target="reason">
              {/* Gate is Flutter's, not the section's: the RFV popup opens when
                  `appointmentReason.length != 0 && status != Cancelled`
                  (`appointment_profile_widget.dart:365-373`). An appointment
                  that is Arrived / CheckedIn / Fulfilled / NoShow keeps an
                  editable RFV — which is the ONLY way to repair an appointment
                  booked against a profile whose `reasonCode` is null, because
                  the schema makes `reasonCode.code` required for ANY save. */}
              {/* Reason trigger — opens the inline picker below */}
              <button
                type="button"
                data-completion-focus
                disabled={reasonPickerDisabled}
                onClick={() => { setReasonOpen(!reasonOpen); if (reasonOpen) setReasonSearch(''); }}
                title={
                  reasonLocked
                    ? 'Reason for visit cannot be changed on a cancelled appointment'
                    : reasonChoices.length === 0
                      ? 'No reasons for visit are configured for this practitioner'
                      : undefined
                }
                className={cn(
                  'w-full flex items-center justify-between gap-2 px-3 h-10 rounded-t-md text-sm text-text-primary disabled:opacity-60 disabled:cursor-not-allowed',
                  reasonOpen && !reasonPickerDisabled
                    ? 'bg-blue-100 border border-brand-primary border-b-0'
                    : 'rounded-b-md bg-grey-100 hover:bg-grey-200 border border-transparent',
                )}
              >
                <span className="truncate text-left flex-1">
                  {friendlyReasonDisplay(
                    selectedReason?.display,
                    selectedReason?.code,
                  ) ?? (
                    <span className="text-text-hint">Reason for visit</span>
                  )}
                </span>
                <ChevronDown className={cn('h-4 w-4 text-text-muted flex-shrink-0 transition-transform', reasonOpen && 'rotate-180')} />
              </button>

              {/* Inline reason picker — attached to the trigger, full width */}
              {reasonOpen && !reasonPickerDisabled && (
                <div className="border border-t-0 border-brand-primary rounded-b-md bg-surface-card overflow-hidden">
                  <div className="px-3 py-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-text-hint pointer-events-none" />
                      <input
                        autoFocus
                        placeholder="Search reasons"
                        value={reasonSearch}
                        onChange={(e) => setReasonSearch(e.target.value)}
                        className="w-full h-10 pl-8 pr-3 rounded-lg bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:outline-none focus:ring-1 focus:ring-brand-primary"
                      />
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto scrollbar-hide px-3 pb-3">
                    {(() => {
                      const q = reasonSearch.trim().toLowerCase();
                      const visible = reasonChoices.filter((r) => {
                        if (!q) return true;
                        const label = friendlyReasonDisplay(r.display, r.code) ?? '';
                        return label.toLowerCase().includes(q);
                      });
                      if (visible.length === 0) {
                        return <p className="text-sm text-text-muted text-center py-4">No matching reasons.</p>;
                      }
                      return (
                        <div className="flex flex-wrap gap-2">
                          {visible.map((r) => {
                            const label = friendlyReasonDisplay(r.display, r.code) ?? 'Unnamed reason';
                            const isActive = selectedReason?.code === r.code;
                            return (
                              <button
                                key={r.code}
                                type="button"
                                onClick={() => {
                                  setValue('reasonCode', { code: r.code, display: friendlyReasonDisplay(r.display, r.code) ?? '', definition: r.definition }, { shouldValidate: true });
                                  setReasonOpen(false);
                                  setReasonSearch('');
                                }}
                                className={cn(
                                  'h-8 px-3 rounded-lg border text-sm font-medium',
                                  isActive
                                    ? 'bg-brand-primary text-white border-brand-primary'
                                    : 'bg-surface-card border-border-default text-text-primary hover:border-brand-primary hover:bg-blue-100',
                                )}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Billing profile row — Flutter parity:
              - "+" opens the procedure-codes popup
              - The dropdown opens the billing-profiles popover */}
          <div className="px-3 pb-3 flex items-center gap-2">
            <Popover open={procedureOpen && !billingLocked} onOpenChange={setProcedureOpen}>
              <PopoverTrigger
                render={
                  <button
                    type="button"
                    className="h-10 w-10 flex items-center justify-center rounded-md text-brand-primary hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!billingProfileId || billingLocked}
                    aria-label="Add procedure codes"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                }
              />
              <PopoverContent align="start" className="w-[320px] p-0">
                <ProcedureCodesPopupBody
                  onClose={() => setProcedureOpen(false)}
                />
              </PopoverContent>
            </Popover>

            <Popover open={billingOpen && !billingLocked} onOpenChange={(o) => { setBillingOpen(o); if (!o) setBillingSearch(''); }}>
              <PopoverTrigger
                render={
                  <button
                    type="button"
                    disabled={billingLocked}
                    className="flex-1 flex items-center justify-between gap-2 px-3 h-10 rounded-md bg-grey-100 hover:bg-grey-200 text-sm text-text-primary disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-grey-100"
                  >
                    <span className="truncate">
                      {billingProfileName
                        ?? (preBook.isPending
                          ? 'Loading billing profile…'
                          : 'No billing profile')}
                    </span>
                    <ChevronDown className="h-4 w-4 text-text-muted flex-shrink-0" />
                  </button>
                }
              />
              <PopoverContent align="end" className="w-[280px] p-0">
                <div className="px-3 py-2 border-b border-border-subtle">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-text-hint pointer-events-none" />
                    <input
                      autoFocus
                      placeholder="Search"
                      value={billingSearch}
                      onChange={(e) => setBillingSearch(e.target.value)}
                      className="w-full h-10 pl-8 pr-3 rounded-lg bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    />
                  </div>
                </div>
                <div className="max-h-[264px] overflow-y-auto scrollbar-hide py-1">
                  {(() => {
                    const q = billingSearch.trim().toLowerCase();
                    const visible = (billingProfilesAll ?? []).filter((b) => {
                      if (!q) return true;
                      return (b.name ?? '').toLowerCase().includes(q);
                    });
                    if (visible.length === 0) {
                      return (
                        <p className="text-sm text-text-muted text-center py-6">
                          No matching billing profiles.
                        </p>
                      );
                    }
                    return visible.map((b, i) => {
                      const isSelected = !!b.id && b.id === billingProfileId;
                      const label = formatBillingProfile(b);
                      return (
                        <button
                          key={b.id || `bp-${i}`}
                          type="button"
                          onClick={() => {
                            if (!b.id) return;
                            setValue('billingProfileId', b.id);
                            setValue('billingProfileName', label);
                            setBillingOpen(false);
                            setBillingSearch('');
                          }}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-grey-100 text-sm text-text-primary"
                        >
                          <span className="truncate">{label}</span>
                          {isSelected ? (
                            <Check className="h-4 w-4 text-brand-primary flex-shrink-0" />
                          ) : null}
                        </button>
                      );
                    });
                  })()}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* F-SCH-137: free-of-charge follow-up banner. Verbatim Flutter copy
              (`appointment_profile_widget.dart:672`) + green-check glyph. Sits
              inside the profile card, after the billing row. */}
          {showFreeFollowUpBanner ? (
            <div className="flex items-start gap-2 px-3 pb-3">
              <Check
                className="h-5 w-5 text-status-success flex-shrink-0"
                aria-hidden
              />
              <p className="text-[length:var(--type-body-size)] text-text-primary">
                Eligible for a free of charge follow-up.
              </p>
            </div>
          ) : null}
        </div>

        {/* Edit-mode profile picker — Flutter `appointment_profile_widget.dart`
            edit-flow renders the alternative-profile grid below the
            selected card. Toggled by the pencil button above. */}
        {profileEditOpen && !profileEditLocked && profiles.length > 0 && (
          <div className="mt-3 space-y-2">
            {location?.name ? (
              <p className="text-xs font-medium text-text-muted">
                {location.name}
              </p>
            ) : null}
            <div className="grid grid-cols-3 gap-2">
              {profiles.map((profile, i) => {
                const Icon = locationIcon(profile.location?.name ?? location?.name ?? '');
                const tint = parseColor(profile.color);
                const isCurrent = profile.id === selectedProfileId;
                return (
                  <button
                    key={profile.id || `edit-profile-${i}`}
                    type="button"
                    onClick={() => {
                      selectProfile(profile);
                      setProfileEditOpen(false);
                    }}
                    title={profile.profileName}
                    className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-colors min-w-0 ${
                      isCurrent
                        ? 'border-brand-primary bg-blue-100'
                        : 'border-border-default bg-surface-card hover:border-brand-primary hover:bg-blue-100'
                    }`}
                  >
                    <span
                      aria-hidden
                      className="flex items-center justify-center h-8 w-8 rounded-md flex-shrink-0"
                      style={{ backgroundColor: categoryColor(tint, 100, 'var(--color-grey-100)'), color: categoryColor(tint, 600, 'var(--color-grey-600)') }}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-text-primary truncate leading-tight">
                        {profile.profileName}
                      </p>
                      {profile.speciality?.display ? (
                        <p className="text-xs text-text-muted truncate leading-tight">
                          {profile.speciality.display}
                        </p>
                      ) : null}
                      <p className="text-xs text-text-hint leading-tight">
                        {profile.minutesDuration ?? 0}m
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </>
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-grey-200 animate-pulse" />
          ))}
        </div>
      ) : profiles.length > 0 ? (
        <div className="space-y-2">
          {/* Sub-header — Flutter `appointment_profile_widget.dart:711` shows
              the appointment's currently-picked location name uppercase. */}
          {location?.name ? (
            <p className="text-xs font-medium text-text-muted">
              {location.name}
            </p>
          ) : null}
          {/* 3-column grid — Flutter `appointment_profile_widget.dart:715-820`
              uses GridView.count(crossAxisCount: 3, childAspectRatio: 2.0). */}
          <div className="grid grid-cols-3 gap-2">
            {profiles.map((profile, i) => {
              const Icon = locationIcon(profile.location?.name ?? location?.name ?? '');
              const tint = parseColor(profile.color);
              return (
                <button
                  key={profile.id || `profile-${i}`}
                  type="button"
                  onClick={() => selectProfile(profile)}
                  title={profile.profileName}
                  className="flex items-center gap-2 p-2 rounded-lg border border-border-default bg-surface-card hover:border-brand-primary hover:bg-blue-100 text-left transition-colors min-w-0"
                >
                  <span
                    aria-hidden
                    className="flex items-center justify-center h-8 w-8 rounded-md flex-shrink-0"
                    style={{ backgroundColor: categoryColor(tint, 100, 'var(--color-grey-100)'), color: categoryColor(tint, 600, 'var(--color-grey-600)') }}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-text-primary truncate leading-tight">
                      {profile.profileName}
                    </p>
                    {profile.speciality?.display ? (
                      <p className="text-xs text-text-muted truncate leading-tight">
                        {profile.speciality.display}
                      </p>
                    ) : null}
                    <p className="text-xs text-text-hint leading-tight">
                      {profile.minutesDuration ?? 0}m
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-sm text-text-muted py-2">
          No appointment profiles configured for this provider. Ask an administrator to set up profiles in Settings, or choose a different provider.
        </p>
      )}
    </div>
  );
}

/**
 * Some reason-for-visit rows arrive with `display` populated as the same
 * UUID as `code` (backend bug — a value-set entry without a localised
 * label). Hide the UUID rather than rendering it raw; if a sibling code
 * looks human-readable we use that instead. Flutter falls back to an
 * empty placeholder for these too (appointment_profile_widget.dart:384).
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function friendlyReasonDisplay(
  display: string | null | undefined,
  code: string | null | undefined,
): string | null {
  const d = (display ?? '').trim();
  if (d && !UUID_RE.test(d)) return d;
  const c = (code ?? '').trim();
  if (c && !UUID_RE.test(c)) return c;
  return null;
}

/** Match the location chip icons used elsewhere in the panel. */
function locationIcon(name: string): ComponentType<SVGProps<SVGSVGElement>> {
  switch (name) {
    case 'Office':
      return Building2;
    case 'Home':
      return Home;
    case 'Virtual':
      return Video;
    default:
      return MapPin;
  }
}


/**
 * Profile color may arrive as Flutter ARGB int string ("4278231126"),
 * hex with or without #, or null. Default to muted grey if unparseable.
 */
function parseColor(raw: string | undefined | null): string {
  if (!raw) return PROFILE_COLOR_FALLBACK_HEX;
  if (raw.startsWith('#')) return raw;
  if (/^[0-9a-fA-F]{6,8}$/.test(raw)) return `#${raw.slice(-6)}`;
  const argb = Number(raw);
  if (Number.isFinite(argb)) {
    const hex = (argb & 0xffffff).toString(16).padStart(6, '0');
    return `#${hex}`;
  }
  return PROFILE_COLOR_FALLBACK_HEX;
}

/**
 * ProcedureCodesPopupBody — Flutter parity for the "Add Procedure Codes"
 * popup body (encounter_module/.../add_billing_procedure_popup.dart).
 *
 * Tabs: Provider Fees / All — both hit
 * `POST /api/Billing/Services/Fees` with the body's `type` flipped
 * between "provider" and "all". A search debounces to the same endpoint.
 *
 * Each row's "+" toggles the service in the form's `addedServices` list.
 * `serviceType` is "Default" for Provider Fees rows (Flutter parity in
 * `getType()` — `isProviderFeeSearch ? 'Default' : 'all'`) and "all" for
 * the catalog tab; this drives `IndividualService.type` on
 * PreBookCalculatePrice when that re-fire ships in a follow-up.
 */
function ProcedureCodesPopupBody({ onClose }: { onClose: () => void }) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const providerId = watch('providerId');
  const location = watch('location');
  const patientId = watch('patientId');
  const billingProfileName = watch('billingProfileName');
  const addedServices = watch('addedServices');

  const [tab, setTab] = useState<'provider' | 'all'>('provider');
  const [search, setSearch] = useState('');

  // Searches server-side on EVERY keystroke — no debounce. Requested by
  // Andrew 2026-08-25 alongside the same change to the billing/claims
  // ServicePickerDialog, so the two Add-service surfaces behave identically.
  //
  // One POST per keystroke instead of one per pause. React Query dedupes
  // identical keys (so backspacing hits cache), `keepPreviousData` on the hook
  // keeps the current rows up while the next response lands, and each request
  // carries an abort signal. A small debounce is the knob if this proves too
  // chatty — deliberately not applied now.
  const { data, isLoading, isFetching } = useServiceFees(
    providerId
      ? {
          providerId,
          locationId: location?.id ?? null,
          patientId: patientId ?? null,
          type: tab,
          query: search || undefined,
        }
      : null,
  );

  const results = data?.results ?? [];
  const selectedIds = new Set(addedServices.map((s) => s.id));

  const toggleService = (svc: ServiceFee) => {
    if (!svc.id) return;
    if (selectedIds.has(svc.id)) {
      setValue(
        'addedServices',
        addedServices.filter((s) => s.id !== svc.id),
      );
    } else {
      setValue('addedServices', [
        ...addedServices,
        {
          id: svc.id,
          serviceType: tab === 'provider' ? 'Default' : 'all',
          procedureCode: svc.procedureCode?.code,
          displayName: svc.procedureCode?.displayName,
          fee: svc.fees?.[0]?.fee,
        },
      ]);
    }
  };

  const formatFeeRow = (svc: ServiceFee): string => {
    const code = svc.procedureCode?.code ?? '';
    const fee = svc.fees?.[0]?.fee;
    const feeStr = typeof fee === 'number' ? `AED${fee}` : '';
    return [code, feeStr].filter(Boolean).join(' • ');
  };

  return (
    <div className="overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <p className="text-sm font-bold text-text-primary">
          Add Procedure Codes
        </p>
        <button
          type="button"
          onClick={onClose}
          className="p-1 hover:bg-grey-100 rounded"
          aria-label="Close"
        >
          <X className="h-4 w-4 text-brand-primary" />
        </button>
      </div>

      <div className="px-4 pt-3 pb-2 space-y-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-3 h-4 w-4 text-text-hint pointer-events-none" />
            <input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-10 pl-8 pr-3 rounded-lg bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>
          <button
            type="button"
            onClick={() => setTab('provider')}
            className={cn(
              'text-sm font-semibold flex-shrink-0',
              tab === 'provider' ? 'text-brand-primary' : 'text-text-muted',
            )}
          >
            Provider Fees
          </button>
          <button
            type="button"
            onClick={() => setTab('all')}
            className={cn(
              'text-sm font-semibold flex-shrink-0',
              tab === 'all' ? 'text-brand-primary' : 'text-text-muted',
            )}
          >
            All
          </button>
        </div>

        {billingProfileName ? (
          <div className="space-y-1 pb-2 border-b border-border-subtle">
            <p className="text-xs font-medium text-text-muted">
              Selected services
            </p>
            <p className="text-sm text-text-primary">{billingProfileName}</p>
            {addedServices.length > 0 ? (
              <ul className="pt-1 space-y-1">
                {addedServices.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-2 text-xs text-text-muted"
                  >
                    <span className="truncate flex-1">
                      {s.displayName ?? s.procedureCode ?? s.id}
                    </span>
                    {typeof s.fee === 'number' ? (
                      <span className="text-text-hint">AED{s.fee}</span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        setValue(
                          'addedServices',
                          addedServices.filter((x) => x.id !== s.id),
                        )
                      }
                      className="p-1 hover:bg-grey-100 rounded"
                      aria-label="Remove service"
                    >
                      <X className="h-3 w-3 text-status-error" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          'max-h-[264px] overflow-y-auto overflow-x-hidden scrollbar-hide px-4 pb-3',
          // Hide scrollbar (Webkit + Firefox) — Flutter parity. List
          // remains scrollable with wheel / touch / keyboard.
          '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {isLoading ? (
          <div className="space-y-2 py-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 rounded bg-grey-200 animate-pulse" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-6">
            {isFetching ? 'Searching…' : 'No matching services.'}
          </p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {results.map((svc, i) => {
              const isAdded = !!svc.id && selectedIds.has(svc.id);
              return (
                <li
                  key={svc.id || `svc-${i}`}
                  className="flex items-start gap-2 py-3 min-w-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-text-primary truncate">
                      {svc.procedureCode?.displayName ?? '—'}
                    </p>
                    {svc.shortDescription ? (
                      <p className="text-xs text-text-muted leading-snug break-words">
                        {svc.shortDescription}
                      </p>
                    ) : null}
                    <p className="text-xs text-text-muted truncate">
                      {formatFeeRow(svc)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleService(svc)}
                    className={cn(
                      'p-1 rounded-md flex-shrink-0',
                      isAdded
                        ? 'text-status-error hover:bg-status-error-bg'
                        : 'text-brand-primary hover:bg-blue-100',
                    )}
                    aria-label={isAdded ? 'Remove service' : 'Add service'}
                  >
                    {isAdded ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
